import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SubscriptionPlan, SubscriptionFeatures, PlanTierOption, PlanTier } from "@/types/subscription";
import { useAuth } from "@/components/AuthProvider";
import { PRO_TIER, PREMIUM_TIER, PRO_TIERS, PREMIUM_TIERS } from "@/components/plans/types";
import { useSubscriptionStatusCheck } from "./useSubscriptionStatusCheck";

export function useSubscription() {
  const { user } = useAuth();
  
  // Initialize subscription status check
  useSubscriptionStatusCheck();

  const { data: plans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .order("price");

      if (error) throw error;
      
      // Transform the data to ensure features match our SubscriptionFeatures type
      return data.map(plan => {
        const rawFeatures = plan.features as Record<string, boolean>;
        const features: SubscriptionFeatures = {
          calendar: !!rawFeatures.calendar,
          workouts: !!rawFeatures.workouts,
          training_sessions: !!rawFeatures.training_sessions,
          progress: !!rawFeatures.progress,
          assessments: !!rawFeatures.assessments,
          public_profile: !!rawFeatures.public_profile,
          nutrition: plan.type === 'premium', // Only premium plans have nutrition
          ai_workouts: plan.type === 'premium', // Only premium plans have AI workouts
        };

        return {
          ...plan,
          features,
          tier: plan.type === 'pro' ? 'pro_10' as PlanTier : 'premium_500' as PlanTier // Default tier
        } as SubscriptionPlan;
      });
    },
  });

  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["user-subscription", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("subscription_plan, subscription_end_date, subscription_tier, subscription_status")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        return {
          subscription_plan: null,
          subscription_end_date: null,
          subscription_tier: null,
          subscription_status: null
        };
      }
      
      return data;
    },
    enabled: !!user?.id,
  });

  const isLoading = isLoadingPlans || isLoadingProfile;

  // Get current tier details based on subscription plan and tier
  const getCurrentTierDetails = () => {
    if (!profileData?.subscription_tier) return null;

    const planType = profileData.subscription_plan;
    const tierName = profileData.subscription_tier as PlanTier;

    if (planType === 'pro') {
      return PRO_TIERS.find(tier => tier.tier === tierName);
    } else if (planType === 'premium') {
      return PREMIUM_TIERS.find(tier => tier.tier === tierName);
    }

    return null;
  };

  // Check if subscription is actually active (not expired)
  const isSubscriptionActive = () => {
    if (!profileData) return false;
    
    // If no subscription plan or free plan
    if (!profileData.subscription_plan || profileData.subscription_plan === 'free') {
      return false;
    }
    
    // If status is not active or trialing
    if (!profileData.subscription_status || 
        !['active', 'trialing'].includes(profileData.subscription_status)) {
      return false;
    }
    
    // If there's an end date, check if it's in the future
    if (profileData.subscription_end_date) {
      const endDate = new Date(profileData.subscription_end_date);
      const now = new Date();
      if (endDate <= now) {
        return false;
      }
    }
    
    return true;
  };

  // Get effective current plan (considering expiration)
  const getEffectivePlan = () => {
    if (!profileData?.subscription_plan) return 'free';
    
    // If subscription is not active, treat as expired but keep original plan name
    // This allows UI to show "Premium (Expired)" instead of reverting to "free"
    return profileData.subscription_plan;
  };

  return {
    plans,
    currentPlan: getEffectivePlan(),
    currentTier: getCurrentTierDetails(),
    subscriptionEndDate: profileData?.subscription_end_date,
    subscriptionStatus: profileData?.subscription_status,
    isSubscriptionActive: isSubscriptionActive(),
    isLoading
  };
}
